import { GearDefinition, GearElement, GearType, Point, SnapPoint, SNAP_DISTANCE } from './types'

/**
 * Gear types rendered as horizontal spans: end glyphs stay fixed-size and only
 * the gap between them stretches when width changes.
 *
 * Audited gear list:
 *  - re-anchor  → span (left anchor plate + right anchor plate with rope gap)
 *  - deviation  → NOT a span (single contact point; uniform icon, no horizontal gap)
 *  - stretcher  → NOT a span (wider aspect but uniform icon, no independent end glyphs)
 *  - i-beam-h/v → handled separately via one-axis transformer constraints
 *  - all others → uniform square icons with centered snap points
 *
 * Add new entries here when introducing gear with independent end glyphs that
 * should keep fixed size while the gap between them stretches.
 */
export const SPAN_GEAR_TYPES: ReadonlySet<GearType> = new Set(['re-anchor'])

export const GEAR_DEFINITIONS: Record<GearDefinition['type'], GearDefinition> = {
  anchor: {
    type: 'anchor',
    label: 'Anchor',
    color: '#2563eb',
    snapPoints: [
      { id: 'bottom', label: 'Anchor point', role: 'anchor', x: 32, y: 58 },
      { id: 'center', label: 'Center', role: 'generic', x: 32, y: 32 },
    ],
  },
  pulley: {
    type: 'pulley',
    label: 'Pulley',
    color: '#475569',
    snapPoints: [
      { id: 'sheave', label: 'Sheave', role: 'pulley-sheave', x: 32, y: 34 },
      { id: 'becket', label: 'Becket', role: 'connector', x: 32, y: 58 },
      { id: 'top', label: 'Top connection', role: 'connector', x: 32, y: 6 },
    ],
  },
  deviation: {
    type: 'deviation',
    label: 'Deviation',
    color: '#64748b',
    snapPoints: [
      { id: 'contact', label: 'Deviation contact', role: 'deviation-contact', x: 32, y: 32 },
      { id: 'anchor', label: 'Deviation anchor', role: 'anchor', x: 32, y: 7 },
    ],
  },
  carabiner: {
    type: 'carabiner',
    label: 'Carabiner',
    color: '#64748b',
    snapPoints: [
      { id: 'bottom', label: 'Bottom', role: 'connector', x: 32, y: 56 },
      { id: 'top', label: 'Top', role: 'connector', x: 32, y: 8 },
    ],
  },
  'rope-grab': {
    type: 'rope-grab',
    label: 'Rope Grab',
    color: '#64748b',
    snapPoints: [
      { id: 'rope', label: 'Rope channel', role: 'connector', x: 32, y: 32 },
      { id: 'eye', label: 'Eye', role: 'connector', x: 32, y: 58 },
    ],
  },
  descender: {
    type: 'descender',
    label: 'Descender',
    color: '#475569',
    snapPoints: [
      { id: 'attachment', label: 'Harness attachment', role: 'connector', x: 32, y: 8 },
      { id: 'rope', label: 'Rope channel', role: 'connector', x: 32, y: 32 },
      { id: 'brake', label: 'Brake strand', role: 'connector', x: 32, y: 56 },
    ],
  },
  're-anchor': {
    type: 're-anchor',
    label: 'Re-anchor',
    color: '#2563eb',
    // Snap x values are nominal; getGearSnapPoints overrides for span gear so
    // the left anchor stays at a fixed inset from the left edge and the right
    // anchor stays at a fixed inset from the right edge regardless of width.
    snapPoints: [
      { id: 'top', label: 'Top anchor', role: 'anchor', x: 10, y: 8 },
      { id: 'bottom', label: 'Right anchor', role: 'anchor', x: 54, y: 8 },
    ],
  },
  technician: {
    type: 'technician',
    label: 'Technician',
    color: '#ea580c',
    snapPoints: [
      { id: 'harness', label: 'Harness', role: 'harness', x: 32, y: 34 },
      { id: 'head', label: 'Head', role: 'generic', x: 32, y: 10 },
    ],
  },
  casualty: {
    type: 'casualty',
    label: 'Casualty',
    color: '#dc2626',
    snapPoints: [
      { id: 'harness', label: 'Harness', role: 'harness', x: 32, y: 36 },
      { id: 'head', label: 'Head', role: 'generic', x: 14, y: 28 },
    ],
  },
  load: {
    type: 'load',
    label: 'Load',
    color: '#475569',
    snapPoints: [
      { id: 'attachment', label: 'Load attachment', role: 'load', x: 32, y: 8 },
      { id: 'center', label: 'Center', role: 'load', x: 32, y: 32 },
    ],
  },
  'i-beam-h': {
    type: 'i-beam-h',
    label: 'I-Beam (H)',
    color: '#6B7280',
    snapPoints: [
      { id: 'top', label: 'Top flange', role: 'anchor', x: 32, y: 3 },
      { id: 'bottom', label: 'Bottom flange', role: 'anchor', x: 32, y: 61 },
    ],
  },
  'i-beam-v': {
    type: 'i-beam-v',
    label: 'I-Beam (V)',
    color: '#6B7280',
    snapPoints: [
      { id: 'left', label: 'Left flange', role: 'anchor', x: 3, y: 32 },
      { id: 'right', label: 'Right flange', role: 'anchor', x: 61, y: 32 },
    ],
  },
  stretcher: {
    type: 'stretcher',
    label: 'Stretcher',
    color: '#7C3AED',
    // Snap x/y are in the 64-unit gear space but the icon SVG uses a 120x56
    // viewBox; coords below are the visual features converted via
    // (svgX * 64/120, svgY * 64/56) so the snaps land on the basket despite
    // the non-square 130x60 element (apex svg60,3; bridle roots svg22/98,30).
    snapPoints: [
      { id: 'master', label: 'Bridle master', role: 'load', x: 32, y: 3 },
      { id: 'head', label: 'Head bridle', role: 'connector', x: 12, y: 34 },
      { id: 'foot', label: 'Foot bridle', role: 'connector', x: 52, y: 34 },
    ],
  },
  edge: {
    type: 'edge',
    label: 'Edge Transition',
    color: '#374151',
    snapPoints: [
      { id: 'roller', label: 'Edge roller', role: 'deviation-contact', x: 40, y: 26 },
      { id: 'base', label: 'Structure', role: 'generic', x: 16, y: 50 },
    ],
  },
  'edge-left': {
    type: 'edge-left',
    label: 'Edge Left',
    color: '#374151',
    snapPoints: [
      { id: 'roller', label: 'Edge roller', role: 'deviation-contact', x: 40, y: 26 },
      { id: 'base', label: 'Structure', role: 'generic', x: 16, y: 50 },
    ],
  },
  'edge-right': {
    type: 'edge-right',
    label: 'Edge Right',
    color: '#374151',
    snapPoints: [
      { id: 'roller', label: 'Edge roller', role: 'deviation-contact', x: 24, y: 26 },
      { id: 'base', label: 'Structure', role: 'generic', x: 48, y: 50 },
    ],
  },
}

export const GEAR_PALETTE: GearDefinition[] = [
  GEAR_DEFINITIONS.anchor,
  GEAR_DEFINITIONS.pulley,
  GEAR_DEFINITIONS.deviation,
  GEAR_DEFINITIONS.carabiner,
  GEAR_DEFINITIONS['rope-grab'],
  GEAR_DEFINITIONS.descender,
  GEAR_DEFINITIONS.technician,
  GEAR_DEFINITIONS.casualty,
  GEAR_DEFINITIONS.stretcher,
  GEAR_DEFINITIONS.load,
  GEAR_DEFINITIONS['edge-left'],
  GEAR_DEFINITIONS['edge-right'],
  GEAR_DEFINITIONS['i-beam-h'],
  GEAR_DEFINITIONS['i-beam-v'],
]

// Snap points for anchor-deviation: keeps anchor snap-point IDs so toggling
// subtype doesn't break existing rope/child-gear references, but uses
// deviation roles + positions for correct rope routing behavior.
const ANCHOR_DEVIATION_SNAP_POINTS: SnapPoint[] = [
  { id: 'bottom', label: 'Deviation anchor', role: 'anchor', x: 32, y: 7 },
  { id: 'center', label: 'Deviation contact', role: 'deviation-contact', x: 32, y: 32 },
]

export function getGearSnapPoints(element: GearElement): Array<SnapPoint & { world: Point }> {
  const snapPoints = element.subtype === 'anchor-deviation'
    ? ANCHOR_DEVIATION_SNAP_POINTS
    : GEAR_DEFINITIONS[element.gearType].snapPoints
  const cx = element.x + element.width / 2
  const cy = element.y + element.height / 2
  const angle = (element.rotation * Math.PI) / 180
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)

  const isSpan = SPAN_GEAR_TYPES.has(element.gearType)
  const scaleY = element.height / 64

  return snapPoints.map((point) => {
    let localX: number
    let localY: number

    if (isSpan) {
      // Span gear: end glyphs sit at a fixed inset from each edge (proportional
      // to height, not width) so snaps track the glyphs regardless of stretch.
      const inset = element.height * (point.x / 64)
      localX = point.x <= 32
        ? -(element.width / 2 - inset)
        : (element.width / 2 - element.height * ((64 - point.x) / 64))
      localY = (point.y - 32) * scaleY
    } else {
      const scaleX = element.width / 64
      localX = (point.x - 32) * scaleX
      localY = (point.y - 32) * scaleY
    }

    return {
      ...point,
      world: {
        x: cx + localX * cos - localY * sin,
        y: cy + localX * sin + localY * cos,
      },
    }
  })
}

/** Recursively collect all descendants of a gear (children, grandchildren, …). */
export function getDescendantGears(parentId: string, allGears: GearElement[]): GearElement[] {
  const result: GearElement[] = []
  const visited = new Set<string>([parentId])
  const stack = [parentId]
  while (stack.length > 0) {
    const id = stack.pop()!
    for (const g of allGears) {
      if (g.parentElementId === id && !visited.has(g.id)) {
        visited.add(g.id)
        result.push(g)
        stack.push(g.id)
      }
    }
  }
  return result
}

/**
 * Compute the world position for a child gear so it is centered on the parent's
 * snap point. Returns the top-left { x, y } for the child element.
 */
export function computeChildPosition(
  child: GearElement,
  parentGear: GearElement,
  parentSnapPointId: string,
): Point {
  const parentSnaps = getGearSnapPoints(parentGear)
  const snap = parentSnaps.find((s) => s.id === parentSnapPointId)
  if (!snap) return { x: child.x, y: child.y }
  return {
    x: snap.world.x - child.width / 2,
    y: snap.world.y - child.height / 2,
  }
}

/**
 * Find the nearest snap point on another gear (not including the dragged gear
 * or any of its descendants) within SNAP_DISTANCE. Used for gear-to-gear
 * attachment during drag-end.
 */
export function findNearestParentGearSnap(
  point: Point,
  allGears: GearElement[],
  excludeIds: Set<string>,
  maxDistance = SNAP_DISTANCE,
): { gear: GearElement; snapPointId: string; snapWorld: Point; distance: number } | null {
  let best: { gear: GearElement; snapPointId: string; snapWorld: Point; distance: number } | null = null
  for (const gear of allGears) {
    if (excludeIds.has(gear.id)) continue
    const snaps = getGearSnapPoints(gear)
    for (const snap of snaps) {
      if (snap.role === 'generic') continue
      const d = Math.hypot(snap.world.x - point.x, snap.world.y - point.y)
      if (d > maxDistance) continue
      if (!best || d < best.distance) {
        best = { gear, snapPointId: snap.id, snapWorld: snap.world, distance: d }
      }
    }
  }
  return best
}
